import {useCallback, useEffect, useRef, useState} from 'react';
import {useAuth} from '@common/auth/use-auth';

type PanelId = 'site' | 'waitlist' | 'signature' | 'error';

const panels: {id: PanelId; icon: string; label: string; meta: string}[] = [
  {id: 'site', icon: '▣', label: 'Website', meta: 'Ready'},
  {id: 'waitlist', icon: '♙', label: 'Waitlist', meta: '42'},
  {id: 'signature', icon: '✎', label: 'Signature', meta: 'Ready'},
  {id: 'error', icon: '404', label: 'Error page', meta: 'Ready'},
];

export function HeroHeader() {
  const {isLoggedIn} = useAuth();
  const [videoOpen, setVideoOpen] = useState(false);
  const [active, setActive] = useState<PanelId>('site');
  const activeIndexRef = useRef(0);

  useEffect(() => {
    activeIndexRef.current = panels.findIndex(p => p.id === active);
  }, [active]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      const next = (activeIndexRef.current + 1) % panels.length;
      setActive(panels[next].id);
    }, 4200);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="hero">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      <div className="hero-inner">
        <div className="eyebrow fade-up">
          <span className="spark">&#10022;</span>
          <span className="eyebrow-text">One idea. Everything you need to launch.</span>
        </div>
        <h1 className="fade-up">
          Bring your next idea
          <br />
          <em>to life.</em>
        </h1>
        <p className="hero-sub fade-up">
          Create your website, launch a waitlist, collect leads, design email signatures, and
          publish through AI&mdash;all in one remarkably simple platform.
        </p>
        <div className="hero-actions fade-up">
          <a href={isLoggedIn ? '/dashboard' : '/register'} className="btn-primary-landing">
            {isLoggedIn ? 'Dashboard' : 'Start building free'} <span>&#8599;</span>
          </a>
          <button type="button" className="text-link" onClick={() => setVideoOpen(true)}>
            <span className="play">&#9654;</span> See what Landed can do
          </button>
        </div>

        <div className="creator fade-up">
          <div className="creator-top">
            <span className="creator-status">
              <i /> Landed AI
            </span>
            <span>Creating your launch kit&hellip;</span>
            <button type="button" aria-label="More options">
              &bull;&bull;&bull;
            </button>
          </div>
          <div className="prompt-row">
            <div className="prompt">
              <span className="prompt-icon">&#10022;</span>
              <span>Create a launch page for my new sustainable coffee brand</span>
              <span className="prompt-cursor" />
            </div>
            <button type="button" className="creator-send" aria-label="Generate">
              &#8593;
            </button>
          </div>
          <div className="workspace">
            <aside className="rail">
              <span className="rail-title">Launch kit</span>
              {panels.map(panel => (
                <button
                  key={panel.id}
                  type="button"
                  className={panel.id === active ? 'active' : ''}
                  onClick={() => setActive(panel.id)}
                >
                  <b>{panel.icon}</b>
                  <span>{panel.label}</span>
                  <i>{panel.meta}</i>
                </button>
              ))}
            </aside>
            <div className="canvas">
              <div className="browserbar">
                <span className="lights">
                  <i />
                  <i />
                  <i />
                </span>
                <span className="bb-url">solstice-coffee.landed.site</span>
                <span className="bb-live">&#9679; Live</span>
              </div>

              <div className={`demo-preview${active === 'site' ? ' active' : ''}`}>
                <div className="mini-nav">
                  <strong>SOLSTICE</strong>
                  <span>
                    Our story&nbsp;&nbsp; Journal&nbsp;&nbsp; <b>Join the waitlist</b>
                  </span>
                </div>
                <div className="mini-hero-panel">
                  <span>SMALL-BATCH COFFEE</span>
                  <h2>
                    A brighter
                    <br />
                    kind of roast.
                  </h2>
                  <p>
                    Thoughtfully sourced. Beautifully roasted.
                    <br />
                    Made for slow mornings.
                  </p>
                  <button type="button">Discover Solstice</button>
                  <div className="coffee-art">
                    <span>&#9788;</span>
                  </div>
                </div>
              </div>

              <div className={`demo-preview${active === 'waitlist' ? ' active' : ''}`}>
                <div className="wait-card-panel">
                  <span className="sun">&#9788;</span>
                  <small>YOU&apos;RE EARLY</small>
                  <h2>Be first in line.</h2>
                  <p>
                    Join the Solstice list for early access and a little something for your
                    first order.
                  </p>
                  <div className="fake-input">
                    <span>you@example.com</span>
                    <b>Join waitlist</b>
                  </div>
                  <div className="avatars">
                    <i>S</i>
                    <i>M</i>
                    <i>J</i>
                    <span>42 people joined this week</span>
                  </div>
                </div>
              </div>

              <div className={`demo-preview${active === 'signature' ? ' active' : ''}`}>
                <div className="sig-card-panel">
                  <div className="sig-avatar">AM</div>
                  <div className="sig-info">
                    <div>
                      <h3>Avery Morgan</h3>
                      <p>Founder &middot; Solstice Coffee</p>
                      <div className="sig-rule" />
                      <small>avery@solstice.coffee &nbsp;&middot;&nbsp; solstice.coffee</small>
                    </div>
                    <span className="sig-logo">&#9788;</span>
                  </div>
                </div>
              </div>

              <div className={`demo-preview${active === 'error' ? ' active' : ''}`}>
                <div className="error-card">
                  <span className="big404">404</span>
                  <h2>Looks like this cup is empty.</h2>
                  <p>The page wandered off. Let&rsquo;s get you back to something freshly brewed.</p>
                  <button type="button">Return home</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="proof fade-up">
          <span>Built for ideas that can&rsquo;t wait</span>
          <div>
            <b>30 sec</b>
            <small>to your first site</small>
          </div>
          <div>
            <b>120+</b>
            <small>launch-ready templates</small>
          </div>
          <div>
            <b>One line</b>
            <small>to embed any form</small>
          </div>
        </div>
      </div>
      {videoOpen && <VideoModal onClose={() => setVideoOpen(false)} />}
    </section>
  );
}

function VideoModal({onClose}: {onClose: () => void}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  return (
    <div
      ref={overlayRef}
      className="video-modal-overlay"
      onClick={e => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="video-modal">
        <button className="video-modal-close" onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <video
          ref={videoRef}
          src="/assets/images/icons/landed_video.mp4"
          autoPlay
          controls
          playsInline
          className="video-modal-player"
        />
      </div>
    </div>
  );
}
